import * as React from 'react'

import { Box, Button, List, Text } from '@factorialco/deprecated-design-system'

import HelpCenterLink from 'modules/core/components/HelpCenterLink'
import PanesHeader from 'modules/core/components/PanesHeader'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import PlanPanel from 'modules/billing/components/PlanPanel'
import getPlanLabel from 'modules/billing/lib/getPlanLabel'
import { usePlanSettingsContext } from 'modules/billing/pages/settings/PlansSettingsContext'
import plans, {
  subscriptionPlanOrScheduledPlan,
  subscriptionPriceWithDiscount,
} from 'modules/billing/store/collections/plans'
import subscriptions from 'modules/billing/store/collections/subscriptions'
import { SOLUTIONS } from 'modules/billing/store/models/Plan'
import { TIER } from 'modules/billing/store/models/Plan'
import AddUsersModal from 'modules/expenses/pages/subscription/AddUsersModal'

const getIntervalsLabel = (type: string) => {
  switch (type) {
    case 'month':
      return i18n.t('billing.intervals.month')
    case 'year':
      return i18n.t('billing.intervals.year')
    default:
      throw new Error(`Unknown translation for type: ${type}`)
  }
}

interface Props {
  expensesUserCount: number
  onCTAClick: () => void | Promise<void>
}

const YourPlan = ({ expensesUserCount, onCTAClick }: Props) => {
  const factorialChargeBee = hasFeature('dev_chargebee')
  const subscription = subscriptions.current
  const plan = plans.current
  const planLabel = getPlanLabel(plan.type)
  const interval = subscription.scheduledInterval || plan.interval
  const intervalLabel = getIntervalsLabel(interval ?? '')
  const price = React.useRef<number>(subscriptionPriceWithDiscount(subscription)).current
  const customerPortal = subscription.chargebeeCustomerPortal
  const [addUsersModalOpen, setAddUsersModalOpen] = React.useState(false)
  const { featureAvailabilities } = usePlanSettingsContext()

  const { navigation } = useNavigation()

  const openCustomerPortal = () => {
    if (factorialChargeBee) {
      window?.open(customerPortal, '_blank')?.focus()
    }
  }

  const solutionTranslation = () => {
    if (plan.solutions === SOLUTIONS.coreTalent) {
      return i18n.t('billing.bill.solutions.core_and_talent')
    } else if (plan.solutions === SOLUTIONS.coreTime) {
      return i18n.t('billing.bill.solutions.core_and_time')
    } else if (plan.solutions === SOLUTIONS.completeBundle) {
      return i18n.t('billing.bill.solutions.complete_bundle')
    } else {
      return ''
    }
  }

  const tierTranslation = () => {
    if (plan.tier === TIER.business) {
      return i18n.t('billing.bill.tier.business')
    } else if (plan.tier === TIER.enterprise) {
      return i18n.t('billing.bill.tier.enterprise')
    } else if (plan.tier === TIER.certification) {
      return i18n.t('billing.bill.tier.certification')
    } else {
      return ''
    }
  }

  if (plan.isLegacy && !factorialChargeBee) return null

  return (
    <>
      {!factorialChargeBee && (
        <Box>
          <div>
            <PanesHeader
              illustration="multiple-buildings"
              title={i18n.t('billing.your_plan.title')}
              description={i18n.t('billing.your_plan.description')}
            />
            <Box paddingBottom="s32">
              <HelpCenterLink name="subscription_payments">
                {i18n.t('billing.your_plan.helpcenter_link')}
              </HelpCenterLink>
            </Box>
            <Button type="primary" onClick={onCTAClick}>
              {i18n.t('pricing_plans.upgrade_plan')}
            </Button>
          </div>
          <PlanPanel.Container title={planLabel}>
            <PlanPanel.Row label={i18n.t('billing.bill.solutions.title')}>
              {solutionTranslation()}
            </PlanPanel.Row>
            <PlanPanel.Row label={i18n.t('billing.bill.tier.title')}>
              {tierTranslation()}
            </PlanPanel.Row>
            <PlanPanel.Row label={i18n.t('billing.bill.period')}>{intervalLabel}</PlanPanel.Row>
            <PlanPanel.Row label={i18n.t('billing.bill.price_per_employee')}>
              {i18n.t('billing.bill.per_month', {
                perMonth: subscriptionPlanOrScheduledPlan(subscription).priceWithCurrency(price),
              })}
            </PlanPanel.Row>
            <PlanPanel.Row label={i18n.t('billing.bill.employees_created')}>
              {subscription.attributes.current_cycle_quantity_on_payment_gateway}
            </PlanPanel.Row>
          </PlanPanel.Container>
        </Box>
      )}
      {factorialChargeBee && (
        <Box gap="s24">
          <Box flexDirection="row">
            <Box flexDirection="row" flexGrow>
              <PanesHeader
                illustration="credit-card"
                title={i18n.t('billing.subscription.title')}
                description={i18n.t('billing.subscription.description')}
              />
            </Box>
            <Box
              flexDirection="row"
              justifyContent="flexEnd"
              alignItems="center"
              flexGrow
              maxHeight="s64"
            >
              <List>
                <List.Item>
                  <Box gap="s12" minWidth="s400">
                    <Box flexDirection="row" justifyContent="spaceBetween" gap="s16">
                      <Box>
                        <Text size="300" weight="semibold">
                          {i18n.t('billing.subscription.manage_account')}
                        </Text>
                      </Box>
                      <Button
                        onClick={openCustomerPortal}
                        type="secondary"
                        iconName="arrow-right"
                      />
                    </Box>
                  </Box>
                </List.Item>
              </List>
            </Box>
          </Box>
          {featureAvailabilities.expensesPerUserPricing && (
            <Box flexDirection="row">
              <Box flexDirection="row" flexGrow>
                <PanesHeader
                  illustration="user-male-female"
                  title={i18n.t('billing.subscription.manage_users.title')}
                  description={i18n.t('billing.subscription.manage_users.description')}
                />
              </Box>

              <Box justifyContent="flexEnd" flexDirection="row" gap="s12" flexGrow>
                <List>
                  <List.Item>
                    <Box gap="s12" minWidth="s400">
                      <Box flexDirection="row" justifyContent="spaceBetween" gap="s16">
                        <Box>
                          <Text size="300" weight="semibold">
                            {i18n.t('billing.subscription.manage_users.expenses_users')}
                          </Text>
                          <Text size="200" weight="medium">
                            {i18n.tp('billing.subscription.manage_users.expenses_users_count', {
                              count: expensesUserCount,
                            })}
                          </Text>
                        </Box>
                        <Button.Router
                          to={navigation.settings.plans.expenses.fullPath()}
                          type="secondary"
                          iconName="arrow-right"
                        />
                      </Box>
                      <Button onClick={() => setAddUsersModalOpen(true)} iconName="add">
                        {i18n.t('billing.subscription.plans.expenses.actions.add_users')}
                      </Button>
                    </Box>
                  </List.Item>
                </List>
              </Box>
              {addUsersModalOpen && <AddUsersModal onClose={() => setAddUsersModalOpen(false)} />}
            </Box>
          )}
        </Box>
      )}
    </>
  )
}

export default YourPlan
